import React, { useRef, useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Target,
  Star,
  Mail,
  Check,
  ArrowRight,
} from "lucide-react";
import dashboard from "../../assets/images/homePage/Dashboard.png";
import leadManagement from "../../assets/images/homePage/lead-management.png";
import reviewManagement from "../../assets/images/homePage/review-mngmnt.png";
import emailComm from "../../assets/images/homePage/email-thread.png";
import hotelLeadCapture from "../../assets/images/homePage/hotel-lead-capture.png";
import leadManagementHome from "../../assets/images/homePage/lead-management-home.png";
import ArrowImage from "../../assets/images/homePage/filled-arrow.png";

const CHECK_SVG = (
  <Check className="tw-w-4 tw-h-4 tw-text-[#5D44FF] tw-flex-shrink-0 tw-mt-0.5" />
);

const ARROW_SVG = (
  <ArrowRight className="tw-w-4 tw-h-4" />
);

const tabs = [
  {
    label: "Dashboard",
    icon: <LayoutDashboard className="tw-w-4 tw-h-4" />,
    title: "Dashboard",
    subtext: "Get a real-time, centralized view of your hotel's sales, marketing, and reputation performance to drive smarter decisions and faster action.",
    points: [
      "Monitor leads, campaigns, and pipeline progress with live insights.",
      "Track guest reviews and sentiment using the built-in Reputation Manager.",
      "Analyze performance metrics with intuitive visuals and filters.",
    ],
    image: dashboard,
    hasLearnMore: false,
    link: "",
  },
  {
    label: "Lead Management",
    icon: <Target className="tw-w-4 tw-h-4" />,
    title: "AI Driven Lead & Guest Engagement",
    subtext: "Capture, manage, and convert high-quality leads with powerful tools designed for hotel sales teams.",
    points: [
      "Streamline lead capture with web-to-lead forms and custom forms.",
      "Automatically convert data to actionable leads and track their journey.",
      "Manage leads efficiently with a kanban pipeline view and customizable stages.",
    ],
    image: leadManagement,
    hasLearnMore: true,
    link: "/lead-management",
  },
  {
    label: "Review Management",
    icon: <Star className="tw-w-4 tw-h-4" />,
    title: "AI Powered Review Management",
    subtext: "Stay on top of guest feedback and build a stronger online reputation with centralized review tools.",
    points: [
      "Manage reviews from Google and major OTA platforms in one place.",
      "Respond faster using pre-built templates and unified review threads.",
      "Gain insights into guest sentiment to improve service quality.",
    ],
    image: reviewManagement,
    hasLearnMore: false,
    link: "",
  },
  {
    label: "Email Communication",
    icon: <Mail className="tw-w-4 tw-h-4" />,
    title: "Email Communication",
    subtext: "Engage leads with personalized conversations through a unified, professional-grade email experience.",
    points: [
      "Send and receive emails directly from the platform with native integration.",
      "View complete conversation history for better context and follow-ups.",
      "Enhance engagement with threaded emails tailored to each lead.",
    ],
    image: emailComm,
    hasLearnMore: false,
    link: "",
  },
];

const About = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [displayTab, setDisplayTab] = useState(0);
  const [animPhase, setAnimPhase] = useState(""); // "" | "exit" | "enter"
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const tabRefs = useRef([]);

  // Recompute slider position and handle horizontal scrolling centering
  useEffect(() => {
    const updateSlider = () => {
      const el = tabRefs.current[activeTab];
      const container = containerRef.current;
      if (el) {
        setSliderStyle({ left: el.offsetLeft, width: el.offsetWidth });
        
        if (container) {
          const containerWidth = container.offsetWidth;
          const elOffsetLeft = el.offsetLeft;
          const elWidth = el.offsetWidth;
          
          container.scrollTo({
            left: elOffsetLeft - (containerWidth / 2) + (elWidth / 2),
            behavior: "smooth"
          });
        }
      }
    };

    updateSlider();

    // Listen to resize/orientation changes to keep indicator positioned correctly
    window.addEventListener("resize", updateSlider);
    return () => window.removeEventListener("resize", updateSlider);
  }, [activeTab]);

  const handleTabChange = (idx) => {
    if (idx === activeTab || animPhase !== "") return;
    // Phase 1: exit (blur + darken out)
    setAnimPhase("exit");
    setTimeout(() => {
      // Swap content while invisible
      setDisplayTab(idx);
      setActiveTab(idx);
      // Phase 2: enter (blur + brighten in)
      setAnimPhase("enter");
      setTimeout(() => setAnimPhase(""), 380);
    }, 220);
  };

  const t = tabs[displayTab];

  return (
    <section className="tw-py-20 lg:tw-py-28 tw-bg-white tw-overflow-hidden" ref={sectionRef}>
      <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">

        {/* ── Block 1: Capture, Nurture, Convert ── */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-12 lg:tw-gap-20 tw-items-center tw-mb-24">
          <div className="tw-text-left">
            <p className="tw-text-xs tw-font-bold tw-text-[#5D44FF] tw-uppercase tw-tracking-widest tw-mb-3">Lead Management Made Simple</p>
            <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-extrabold tw-text-slate-900 tw-mb-5 tw-leading-tight">
              Capture, Nurture, <span className="tw-text-[#5D44FF]">Convert!</span>
            </h2>
            <p className="tw-text-base tw-text-slate-500 tw-leading-relaxed tw-mb-8">
              Effortlessly Capture, Nurture, and Convert Leads with our integrated lead management. From seamless lead acquisition to personalized nurturing and higher conversion rates, streamline your sales journey like never before.
            </p>
            <ul className="tw-space-y-5 tw-mb-10">
              {[
                { title: "Effortless Lead Capture", sub: "Instantly capture valuable customer details from various sources." },
                { title: "Streamlined Tracking", sub: "Keep every lead organized, ensuring no opportunity is missed." },
              ].map((item, i) => (
                <li key={i} className="tw-flex tw-items-start tw-gap-3">
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5">
                    {CHECK_SVG}
                  </div>
                  <div>
                    <strong className="tw-text-slate-800 tw-text-sm tw-block">{item.title}</strong>
                    <span className="tw-text-sm tw-text-slate-400">{item.sub}</span>
                  </div>
                </li>
              ))}
            </ul>
            <NavLink to="/signup" className="hero-btn-shine tw-inline-flex tw-items-center tw-gap-2 tw-px-7 tw-py-3.5 tw-bg-[#5D44FF] hover:tw-bg-indigo-600 tw-text-white tw-font-bold tw-text-sm tw-rounded-xl tw-shadow-lg tw-shadow-indigo-500/20 tw-transition-all hover:tw-scale-[1.03]">
              Start Now {ARROW_SVG}
            </NavLink>
          </div>
          <div className="tw-flex tw-justify-center">
            {/* img-shadow-indigo defined in index.css */}
            <img src={leadManagementHome} alt="Lead Management" className="tw-max-w-full tw-h-auto img-shadow-indigo" />
          </div>
        </div>

        {/* ── Block 2: Simplify, Delight, Excel ── */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-12 lg:tw-gap-20 tw-items-center tw-mb-24">
          <div className="tw-flex tw-justify-center lg:tw-order-first tw-order-last">
            {/* img-shadow-purple defined in index.css */}
            <img src={hotelLeadCapture} alt="Hotel Lead Capture" className="tw-max-w-full tw-h-auto img-shadow-purple" />
          </div>
          <div className="tw-text-left">
            <p className="tw-text-xs tw-font-bold tw-text-[#5D44FF] tw-uppercase tw-tracking-widest tw-mb-3">Capture More Leads. Book More Stays</p>
            <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-extrabold tw-text-slate-900 tw-mb-5 tw-leading-tight">
              Simplify, Delight, <span className="tw-text-[#5D44FF]">Excel!</span>
            </h2>
            <p className="tw-text-base tw-text-slate-500 tw-leading-relaxed tw-mb-8">
              Integrate bookings from various OTAs and channels, automate queries, and track guest preferences to deliver personalized experiences. Simplify lead management and elevate guest satisfaction effortlessly.
            </p>
            <ul className="tw-space-y-5 tw-mb-10">
              {[
                { title: "Integrated Solutions", sub: "Streamline Operations Tailored for the travel and Hospitality Industry." },
                { title: "Enhanced Guest Experience", sub: "Deliver personalized service and seamless interactions to elevate every guest's stay." },
              ].map((item, i) => (
                <li key={i} className="tw-flex tw-items-start tw-gap-3">
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5">
                    {CHECK_SVG}
                  </div>
                  <div>
                    <strong className="tw-text-slate-800 tw-text-sm tw-block">{item.title}</strong>
                    <span className="tw-text-sm tw-text-slate-400">{item.sub}</span>
                  </div>
                </li>
              ))}
            </ul>
            <NavLink to="/signup" className="hero-btn-shine tw-inline-flex tw-items-center tw-gap-2 tw-px-7 tw-py-3.5 tw-bg-[#5D44FF] hover:tw-bg-indigo-600 tw-text-white tw-font-bold tw-text-sm tw-rounded-xl tw-shadow-lg tw-shadow-indigo-500/20 tw-transition-all hover:tw-scale-[1.03]">
              Book Smarter {ARROW_SVG}
            </NavLink>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100 tw-mb-24" />

        {/* ── Transform Inquiries Section ── */}
        <div className="tw-transition-all tw-duration-700 transition-delay-250">
          {/* Header */}
          <div className="tw-text-center tw-max-w-2xl tw-mx-auto tw-mb-12">
            <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-extrabold tw-text-slate-900 tw-mb-4 tw-leading-tight">
              Transform Inquiries into <span className="tw-text-[#5D44FF]">Bookings</span>
            </h2>
            <p className="tw-text-base tw-text-slate-500 tw-leading-relaxed">
              Never miss an opportunity with our comprehensive lead management system designed specifically for hospitality businesses.
            </p>
          </div>

          {/* Tab Bar with sliding pill indicator */}
          <div ref={containerRef} className="tab-nav-container tw-flex tw-flex-nowrap tw-overflow-x-auto no-scrollbar tw-max-w-full tw-justify-start sm:tw-justify-center tw-gap-0 tw-mb-10 tw-bg-slate-50 tw-rounded-2xl tw-p-1.5 tw-w-fit tw-mx-auto tw-border tw-border-solid tw-border-slate-100 tw-relative">
            {/* Sliding background indicator */}
            <div
              className="tw-absolute tw-top-1.5 tw-bottom-1.5 tw-rounded-xl tw-bg-white tw-shadow-md"
              style={{
                left: sliderStyle.left,
                width: sliderStyle.width,
                transition: "left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 10px rgba(93, 68, 255, 0.13), 0 1px 3px rgba(0,0,0,0.06)",
              }}
            />
            {tabs.map((tab, idx) => {
              const isActive = activeTab === idx;
              return (
                <button
                  key={idx}
                  ref={el => tabRefs.current[idx] = el}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleTabChange(idx)}
                  className={`tw-relative tw-z-10 tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-semibold tw-transition-colors tw-duration-200 tw-outline-none tw-border-none tw-cursor-pointer tw-bg-transparent tw-whitespace-nowrap ${
                    isActive ? "tw-text-[#5D44FF]" : "tw-text-slate-500 hover:tw-text-slate-700"
                  }`}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content Panel */}
          <div className="panel-bg tw-rounded-3xl tw-border tw-border-solid tw-border-slate-100 tw-overflow-hidden tw-shadow-sm">
            <div
              key={displayTab}
              className={`tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 ${
                animPhase === "exit" ? "tab-content-exit" :
                animPhase === "enter" ? "tab-content-enter" : ""
              }`}
            >
              {/* Left: Text */}
              <div className="tw-p-8 lg:tw-p-12 tw-flex tw-flex-col tw-justify-center tw-text-left">
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-[#5D44FF] tw-uppercase tw-tracking-widest tw-mb-5 tw-bg-indigo-50 tw-px-3 tw-py-1.5 tw-rounded-full tw-border tw-border-solid tw-border-indigo-100 tw-w-fit">
                  <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-[#5D44FF] tw-inline-block" />
                  {t.icon} {tabs[activeTab].label}
                </span>
                <h3 className="tw-text-2xl tw-font-extrabold tw-text-slate-900 tw-mb-4 tw-leading-snug">
                  {t.title}
                </h3>
                <p className="tw-text-sm tw-text-slate-500 tw-leading-relaxed tw-mb-7">
                  {t.subtext}
                </p>
                <ul className="tw-space-y-4 tw-mb-8">
                  {t.points.map((point, idx) => (
                    <li key={idx} className="tw-flex tw-items-start tw-gap-3 tw-text-sm tw-text-slate-600">
                      <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5">
                        {CHECK_SVG}
                      </div>
                      {point}
                    </li>
                  ))}
                </ul>
                {t.hasLearnMore && (
                  <NavLink
                    to={t.link}
                    className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-[#5D44FF] hover:tw-gap-3 tw-transition-all tw-w-fit"
                  >
                    Learn More
                    <img src={ArrowImage} alt="Arrow" className="tw-h-4 tw-w-4" loading="lazy" />
                  </NavLink>
                )}
              </div>

              {/* Right: Image */}
              <div className="tw-flex tw-items-center tw-justify-center tw-p-8 lg:tw-p-10 tw-border-t lg:tw-border-l lg:tw-border-t-0 tw-border-r-0 tw-border-b-0 tw-border-solid tw-border-slate-100">
                <img
                  src={t.image}
                  alt={t.label}
                  className="tw-max-w-full tw-h-auto tw-rounded-2xl tw-transition-all tw-duration-200 img-shadow-tab"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Bottom dot nav */}
            <div className="tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100">
              {tabs.map((_, idx) => (
                <button
                  key={idx}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleTabChange(idx)}
                  className={`tw-transition-all tw-duration-300 ${activeTab === idx ? "dot-active" : "dot-inactive"}`}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;
